import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import './AdminPortfolio.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTimes, FaSave, FaImage, FaListUl, FaGlobe, FaCalendarAlt, FaPalette, FaCloudUploadAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { safeFetch } from '../utils/apiUtils';

const AdminPortfolio = () => {
    const [projects, setProjects] = useState([]);
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [imagePreview, setImagePreview] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        service_id: '',
        description: '',
        image: null,
        client: '',
        date: '',
        theme_color: '#0E1629'
    });

    useEffect(() => {
        fetchProjects();
        fetchServices();
    }, []);

    const fetchProjects = async () => {
        setIsLoading(true);
        try {
            const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/projects');
            if (ok) {
                setProjects(Array.isArray(result) ? result : result.data || []);
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
            toast.error('Failed to load projects');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchServices = async () => {
        try {
            const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/services');
            if (ok) {
                setServices(Array.isArray(result) ? result : result.data || []);
            }
        } catch (error) {
            console.error('Error fetching services:', error);
        }
    };

    const openModal = (project = null) => {
        if (project) {
            setEditingProject(project);
            setFormData({
                title: project.title,
                service_id: project.service_id,
                description: project.description,
                image: null,
                client: project.client || '',
                date: project.date || '',
                theme_color: project.theme_color || '#0E1629'
            });
            setImagePreview(project.image_url || project.image);
        } else {
            setEditingProject(null);
            setFormData({
                title: '',
                service_id: services.length > 0 ? services[0].id : '',
                description: '',
                image: null,
                client: '',
                date: '',
                theme_color: '#0E1629'
            });
            setImagePreview(null);
        }
        setIsModalOpen(true);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, image: file });
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        const data = new FormData();
        data.append('title', formData.title);
        data.append('service_id', formData.service_id);
        data.append('description', formData.description);
        data.append('client', formData.client);
        data.append('date', formData.date);
        data.append('theme_color', formData.theme_color);
        if (formData.image) {
            data.append('image', formData.image);
        }

        let url = 'https://aayushrealproperties.in/nimaibackend/public/api/projects';
        if (editingProject) {
            url = `https://aayushrealproperties.in/nimaibackend/public/api/projects/${editingProject.id}`;
            data.append('_method', 'PUT'); // Spoofing PUT for multipart/form-data
        }

        try {
            const { ok, data: errorData } = await safeFetch(url, {
                method: 'POST', // Always POST for multipart mapping
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                },
                body: data
            });

            if (ok) {
                toast.success(editingProject ? 'Project updated successfully' : 'Project created successfully');
                fetchProjects();
                setIsModalOpen(false);
            } else {
                toast.error(errorData.message || 'Failed to save project');
            }
        } catch (error) {
            console.error('Error saving project:', error);
            toast.error('Network error while saving');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this project?')) return;

        try {
            const { ok } = await safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/projects/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                }
            });

            if (ok) {
                setProjects(projects.filter(p => p.id !== id));
                toast.success('Project deleted successfully');
            } else {
                toast.error('Failed to delete project');
            }
        } catch (error) {
            console.error('Error deleting project:', error);
            toast.error('Network error while deleting');
        }
    };

    const filteredProjects = projects.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.service?.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.client || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Portfolio <span>Management</span></h1>
                        <p>Showcase your best projects and innovations.</p>
                    </div>
                    <button className="add-btn" onClick={() => openModal()}>
                        <FaPlus /> Add New Project
                    </button>
                </header>

                <div className="portfolio-controls">
                    <div className="search-bar">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search projects..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="admin-projects-grid">
                    {isLoading ? (
                        <div className="admin-loading-container" style={{ textAlign: 'center', gridColumn: '1/-1', padding: '100px' }}>
                            <div className="admin-loader"></div>
                            <p style={{ marginTop: '20px', color: '#64748b' }}>Loading portfolio...</p>
                        </div>
                    ) : filteredProjects.length > 0 ? (
                        filteredProjects.map((project, index) => (
                            <motion.div
                                key={project.id}
                                className="admin-project-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                            >
                                <div className="project-preview">
                                    <img src={project.image_url || project.image} alt={project.title} />
                                    <div className="project-badge" style={{ backgroundColor: project.theme_color }}>
                                        {project.service?.title || 'General'}
                                    </div>
                                </div>
                                <div className="project-details">
                                    <h3>{project.title}</h3>
                                    <p className="description">{project.description}</p>
                                    <div className="meta">
                                        <span><FaGlobe /> {project.client}</span>
                                        <span><FaCalendarAlt /> {project.date}</span>
                                    </div>
                                    <div className="card-actions">
                                        <button className="edit-btn" onClick={() => openModal(project)}>
                                            <FaEdit /> Edit
                                        </button>
                                        <button className="del-btn" onClick={() => handleDelete(project.id)}>
                                            <FaTrash />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))
                    ) : (
                        <div className="empty-state" style={{ gridColumn: '1/-1', textAlign: 'center', padding: '100px' }}>
                            <h3>No projects found</h3>
                            <p>All ready? Click "Add New Project" to expand your showcase.</p>
                        </div>
                    )}
                </div>

                {/* Add/Edit Modal */}
                <AnimatePresence>
                    {isModalOpen && (
                        <motion.div
                            className="modal-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                className="portfolio-modal"
                                initial={{ y: 50, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 50, opacity: 0 }}
                            >
                                <div className="modal-header">
                                    <h2>{editingProject ? 'Edit Project' : 'New Project'}</h2>
                                    <button className="close-modal" onClick={() => setIsModalOpen(false)}>
                                        <FaTimes />
                                    </button>
                                </div>

                                <form onSubmit={handleSave} className="admin-form">
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label><FaListUl /> Project Title</label>
                                            <input
                                                type="text"
                                                value={formData.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label><FaPalette /> Service Category</label>
                                            <select
                                                value={formData.service_id}
                                                onChange={(e) => setFormData({ ...formData, service_id: e.target.value })}
                                                required
                                            >
                                                {services.map(service => (
                                                    <option key={service.id} value={service.id}>{service.title}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group full-width">
                                            <label><FaImage /> Project Image</label>
                                            <div className="image-upload-wrapper" style={{ position: 'relative', width: '100%', height: '150px', border: '2px dashed #cbd5e1', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'pointer' }}>
                                                {imagePreview ? (
                                                    <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <>
                                                        <FaCloudUploadAlt style={{ fontSize: '2.5rem', color: '#94a3b8' }} />
                                                        <span style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '10px' }}>Click to upload cover image</span>
                                                    </>
                                                )}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label><FaGlobe /> Client</label>
                                            <input
                                                type="text"
                                                value={formData.client}
                                                onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label><FaCalendarAlt /> Date</label>
                                            <input
                                                type="text"
                                                value={formData.date}
                                                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                                placeholder="e.g. Jan 2024"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Theme Color</label>
                                            <div className="color-picker-wrapper">
                                                <input
                                                    type="color"
                                                    value={formData.theme_color}
                                                    onChange={(e) => setFormData({ ...formData, theme_color: e.target.value })}
                                                />
                                                <span>{formData.theme_color}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Short Description</label>
                                        <textarea
                                            rows="3"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            required
                                        ></textarea>
                                    </div>

                                    <div className="modal-footer">
                                        <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                                        <button type="submit" className="btn-primary">
                                            <FaSave /> {editingProject ? 'Update' : 'Create'} Project
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
};

export default AdminPortfolio;
